import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  Image,
  StyleSheet,
  Dimensions,
  TouchableOpacity,
} from 'react-native';

const { width: screenWidth } = Dimensions.get('window');

// This is a simplified version of MomentCard for sharing purposes
// initialPhotoIndex: 0/1 = which side of dual overlay; 2+ = full-bleed that photo (matches feed carousel)
const ShareableMomentCard = ({ moment, privyDid, onRestaurantPress, showDetails = true, initialPhotoIndex = 0 }) => {
  const photoUrls = useMemo(() => {
    const raw = (moment.photoUrls || []).filter((u) => u != null && u !== '');
    if (raw.length > 0) return raw;
    return [moment.frontPhotoUrl, moment.backPhotoUrl].filter(Boolean);
  }, [moment.photoUrls, moment.frontPhotoUrl, moment.backPhotoUrl]);

  const [isFrontPhotoActive, setIsFrontPhotoActive] = useState(() => initialPhotoIndex === 0);
  const [isToggling, setIsToggling] = useState(false);
  const [imagesPreloaded, setImagesPreloaded] = useState(false);

  useEffect(() => {
    if (initialPhotoIndex === 0 || initialPhotoIndex === 1) {
      setIsFrontPhotoActive(initialPhotoIndex === 0);
    }
  }, [initialPhotoIndex]);

  useEffect(() => {
    const preloadImages = async () => {
      if (photoUrls.length === 0) {
        setImagesPreloaded(true);
        return;
      }
      try {
        await Promise.all(photoUrls.map((u) => Image.prefetch(u)));
        setImagesPreloaded(true);
      } catch (error) {
        setImagesPreloaded(true);
      }
    };

    preloadImages();
  }, [photoUrls]);

  // Toggle photo function - instant swap
  const togglePhoto = () => {
    if (isToggling || !imagesPreloaded) return; // Prevent toggles while loading
    
    setIsToggling(true);
    
    // Instant state update
    setIsFrontPhotoActive(!isFrontPhotoActive);
    
    // Brief delay to prevent rapid toggles
    setTimeout(() => {
      setIsToggling(false);
    }, 100);
  };

  // Relative time function
  const getRelativeTime = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (years > 0) return `${years}y`;
    if (months > 0) return `${months}m`;
    if (weeks > 0) return `${weeks}w`;
    if (days > 0) return `${days}d`;
    if (days === 0) return 'today';
    return 'now';
  };

  const renderMedia = () => {
    if (photoUrls.length === 0) {
      return null;
    }

    if (initialPhotoIndex >= 2 && photoUrls[initialPhotoIndex]) {
      return (
        <Image
          source={{ uri: photoUrls[initialPhotoIndex] }}
          style={[styles.singlePhoto, !showDetails && styles.singlePhotoExpanded]}
          resizeMode="cover"
        />
      );
    }

    if (photoUrls.length === 1) {
      return (
        <Image
          source={{ uri: photoUrls[0] }}
          style={[styles.singlePhoto, !showDetails && styles.singlePhotoExpanded]}
          resizeMode="cover"
        />
      );
    }

    const activePhotoUrl = isFrontPhotoActive ? photoUrls[0] : photoUrls[1];
    const overlayPhotoUrl = isFrontPhotoActive ? photoUrls[1] : photoUrls[0];

    return (
      <View style={[styles.dualPhotoContainer, !showDetails && styles.dualPhotoContainerExpanded]}>
        <Image
          source={{ uri: activePhotoUrl }}
          style={styles.dualPhotoBackground}
          resizeMode="cover"
        />
        <TouchableOpacity
          style={[styles.dualPhotoOverlay, !showDetails && styles.dualPhotoOverlayExpanded]}
          onPress={togglePhoto}
          activeOpacity={0.8}
        >
          <Image
            source={{ uri: overlayPhotoUrl }}
            style={styles.dualPhotoForeground}
            resizeMode="cover"
          />
        </TouchableOpacity>
      </View>
    );
  };

  const mediaBlock = renderMedia();

  return (
    <View style={styles.cardContainer}>
      <View style={styles.card}>
        {/* Media Block - Photos first */}
        {mediaBlock && (
          <View style={[styles.mediaContainer, !showDetails && styles.mediaContainerExpanded]}>
            {mediaBlock}
          </View>
        )}

        {/* User Info - At bottom (conditional) */}
        {showDetails && (
          <View style={styles.userInfoContainer}>
            <View style={styles.userInfoRow}>
              <View style={styles.userInfoContent}>
                <Text style={styles.userInfoText}>
                  @{moment.user?.username || 'unknown'} checked in at <Text style={styles.restaurantName}>
                    {moment.restaurant?.name || 'Unknown Restaurant'}
                  </Text>
                  {moment.rating && (
                    <Text style={styles.inlineRatingText}>
                      {' • '}
                      {(() => {
                        const rating = moment.rating || 0;
                        if (rating === 5) {
                          return '🤤 crave';
                        } else if (rating >= 3) {
                          return '😌 solid';
                        } else {
                          return '🫢 toss';
                        }
                      })()}
                    </Text>
                  )}
                </Text>
              </View>
            </View>
            
            {/* Time Display - Bottom Right */}
            <View style={styles.timeContainer}>
              <Text style={styles.timeText}>{moment.relativeTime || getRelativeTime(moment.createdAt)}</Text>
            </View>
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    width: '100%',
    maxWidth: screenWidth - 40, // Account for bottom sheet padding
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 0, // Remove border radius for full photo fill
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    borderWidth: 0, // Remove border
    borderColor: 'transparent', // Remove border color
    overflow: 'hidden',
  },
  userInfoContainer: {
    backgroundColor: '#F5F1ED', // Light DISGO brown
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
    position: 'relative',
  },
  userInfoRow: {
    marginBottom: 4,
  },
  userInfoContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  userInfoText: {
    fontSize: 15,
    fontWeight: '500',
    color: '#1A1A1A',
    lineHeight: 20,
    flex: 1,
  },
  inlineRatingPill: {
    backgroundColor: '#F8F8F8',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#E8E8E8',
    overflow: 'hidden',
    marginLeft: 8,
  },
  inlineRatingText: {
    fontSize: 11,
    color: '#8B8B8B',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  timeContainer: {
    position: 'absolute',
    bottom: 12,
    right: 16,
  },
  timeText: {
    fontSize: 12,
    color: '#8B8B8B',
    fontWeight: '500',
  },
  // checkinText style removed - now part of userInfoText
  restaurantName: {
    textDecorationLine: 'underline',
    color: '#9C6B42',
    fontWeight: '600',
  },
  // Rating styles removed - moved to footer
  mediaContainer: {
    marginHorizontal: 0,
    borderRadius: 0,
    overflow: 'hidden',
    marginTop: 0,
    marginBottom: 0,
    height: 320, // Increased height for With Details mode
    padding: 0, // Ensure no padding
  },
  mediaContainerExpanded: {
    height: 380, // Increased height for expanded mode
  },
  singlePhoto: {
    width: '100%',
    height: '100%', // Fill the container height
  },
  singlePhotoExpanded: {
    height: '100%', // Fill the container height
  },
  dualPhotoContainer: {
    position: 'relative',
    width: '100%',
    height: '100%', // Fill the container height
  },
  dualPhotoContainerExpanded: {
    height: '100%', // Fill the container height
  },
  dualPhotoBackground: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  photoLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  dualPhotoImage: {
    width: '100%',
    height: '100%',
  },
  dualPhotoOverlay: {
    position: 'absolute',
    top: 16,
    left: 16,
    width: 90, // Skinnier overlay for With Details mode
    height: 120,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 3,
    borderColor: '#D4C4B8',
    backgroundColor: '#FFFFFF', // Solid background for efficient shadow calculation
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 6,
    elevation: 6,
  },
  dualPhotoOverlayExpanded: {
    width: 90, // Skinnier overlay for Photo Only mode
    height: 120, // Keep same height
  },
  dualPhotoForeground: {
    width: '100%',
    height: '100%',
  },
});

export default ShareableMomentCard;
